import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import PrintPacketModal from './PrintPacketModal';
import PreSaleCalculator from './PreSaleCalculator';

const DealDeskLite = ({ vehicles: propVehicles, onRefresh }) => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewSale, setShowNewSale] = useState(false);
  const [showCalc, setShowCalc] = useState(false);
  const [editDeal, setEditDeal] = useState(null);
  // Deal whose unified Pre-Print screen is open (replaces the old instant print)
  const [printPacketDeal, setPrintPacketDeal] = useState(null);

  const loadDeals = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.getDeals();
      setDeals(d || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadDeals(); }, [loadDeals]);

  const filtered = deals.filter(d => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (d.stock_number||'').toLowerCase().includes(s) ||
      (d.buyer_name||'').toLowerCase().includes(s) ||
      (d.make||'').toLowerCase().includes(s) ||
      (d.model||'').toLowerCase().includes(s);
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-700 to-teal-800 text-white px-6 py-4 rounded-xl flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold">Deal Desk Lite</h1>
          <p className="text-teal-200 text-xs">Simplified Sale Entry</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowCalc(true)} className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600">
            Calculator
          </button>
          <button onClick={() => setShowNewSale(true)} className="px-4 py-2 bg-white text-teal-800 rounded-lg font-medium hover:bg-teal-50">
            + New Sale
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-neutral-500">{deals.length} sales</span>
        <input type="text" placeholder="Search stock#, buyer, make, model..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="ml-auto px-3 py-2 border rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-teal-500"/>
      </div>

      {/* Deals Table — no prices, no balances, no AR */}
      {loading ? (
        <div className="text-center py-10 text-neutral-500">Loading deals...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-neutral-400">
          {searchTerm ? 'No deals match search' : 'No deals yet — click + New Sale to get started'}
        </div>
      ) : (
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium">Stock #</th>
                <th className="px-3 py-2 text-left text-xs font-medium">Vehicle</th>
                <th className="px-3 py-2 text-left text-xs font-medium">Buyer</th>
                <th className="px-3 py-2 text-left text-xs font-medium">Type</th>
                <th className="px-3 py-2 text-left text-xs font-medium">Date</th>
                <th className="px-3 py-2 text-xs font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.map(d => (
                  <tr key={d.id} className="hover:bg-neutral-50">
                    <td className="px-3 py-2 font-mono text-xs font-bold text-blue-700">{d.stock_number}</td>
                    <td className="px-3 py-2 text-xs">{d.year} {d.make} {d.model}</td>
                    <td className="px-3 py-2 text-xs font-medium">{d.buyer_name}</td>
                    <td className="px-3 py-2">
                      <span className={'text-xs px-2 py-0.5 rounded-full ' + (d.sale_type === 'retail' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700')}>
                        {d.sale_type === 'retail' ? 'Retail' : 'Wholesale'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-neutral-500">
                      {d.sale_date ? new Date(d.sale_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        <button onClick={() => setEditDeal(d)} className="text-xs px-2 py-1 bg-neutral-100 rounded hover:bg-neutral-200">Edit</button>
                        <button onClick={() => setPrintPacketDeal(d)}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                          Forms
                        </button>
                        <a href="https://www.ohioautodealers.com" target="_blank" rel="noopener noreferrer"
                          className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200">Tag</a>
                      </div>
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New Sale Modal */}
      {showNewSale && <NewSaleLiteModal vehicles={propVehicles} onClose={() => { setShowNewSale(false); loadDeals(); onRefresh?.(); }} onComplete={() => { loadDeals(); onRefresh?.(); }} />}

      {/* Edit Deal Modal */}
      {editDeal && <EditDealLiteModal deal={editDeal} onClose={() => setEditDeal(null)} onSave={() => { setEditDeal(null); loadDeals(); }} />}

      {/* Unified Pre-Print screen */}
      {printPacketDeal && (
        <PrintPacketModal mode="deal" deal={printPacketDeal} onClose={() => setPrintPacketDeal(null)} />
      )}

      {/* Pre-Sale Calculator */}
      <PreSaleCalculator isOpen={showCalc} onClose={() => setShowCalc(false)} />
    </div>
  );
};

// Simplified New Sale Modal
const NewSaleLiteModal = ({ vehicles, onClose, onComplete }) => {
  const [step, setStep] = useState(1);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [saleType, setSaleType] = useState('retail');
  const [loading, setLoading] = useState(false);
  const [taxRates, setTaxRates] = useState({});
  const [completedDeal, setCompletedDeal] = useState(null);
  const [showPrintPacket, setShowPrintPacket] = useState(false);
  const [otdPrice, setOtdPrice] = useState('');
  const [otdEditing, setOtdEditing] = useState(false);
  const [form, setForm] = useState({
    buyerFirstName: '', buyerMiddleName: '', buyerLastName: '',
    buyerAddress: '', buyerCity: '', buyerState: 'OH', buyerZip: '',
    buyerPhone: '', buyerEmail: '',
    // isOutOfState starts UNSET — the seller must explicitly pick in-state or
    // out-of-state before the sale can be finalized (drives tax AND which forms print)
    salePrice: '', county: 'Lucas', isOutOfState: null,
    // Out-of-state buyers carry their home-state tax rate (e.g. Michigan 6%) — must be typed
    outOfStateTaxRate: '',
    tradeInValue: 0, docFee: 300, tempTagFee: 20, titleFee: 22.50,
    odometer: ''
  });

  // Shared tax-rate rule for every calculation in this modal
  const effectiveTaxRate = (f, type) => {
    if (type !== 'retail') return 0;
    if (f.isOutOfState) return parseFloat(f.outOfStateTaxRate) || 0;
    return taxRates[f.county] || 7.75;
  };

  useEffect(() => {
    api.getTaxRates().then(d => setTaxRates(d.rates || d)).catch(() => {});
  }, []);

  const available = (vehicles || []).filter(v => v.status !== 'sold');
  const filtered = available.filter(v => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (v.stockNumber||'').toLowerCase().includes(s) ||
      (v.make||'').toLowerCase().includes(s) ||
      (v.model||'').toLowerCase().includes(s) ||
      (v.vin||'').toLowerCase().includes(s);
  });

  const price = parseFloat(form.salePrice) || 0;
  const trade = parseFloat(form.tradeInValue) || 0;
  const doc = parseFloat(form.docFee) || 0;
  const taxable = Math.max(0, price - trade);
  const taxRate = effectiveTaxRate(form, saleType);
  const tax = taxable * taxRate / 100;
  const tempTag = saleType === 'retail' ? (parseFloat(form.tempTagFee) || 0) : 0;
  const titleFee = parseFloat(form.titleFee) || 0;
  const total = price + tax + doc + tempTag + titleFee - trade;

  // Retail deals can't finalize until the in/out-of-state question is answered
  // (and out-of-state needs a typed tax rate)
  const stateChoiceMissing = saleType === 'retail' && form.isOutOfState === null;
  const oosRateMissing = saleType === 'retail' && form.isOutOfState === true && String(form.outOfStateTaxRate).trim() === '';

  const handleSubmit = async () => {
    const buyerFullName = [form.buyerFirstName, form.buyerMiddleName, form.buyerLastName].filter(Boolean).join(' ').trim();
    if (!selected || !buyerFullName || !form.salePrice) {
      alert('Please fill in all required fields');
      return;
    }
    if (stateChoiceMissing) {
      alert('Choose In-State or Out-of-State first — it sets the tax and which forms print.');
      return;
    }
    if (oosRateMissing) {
      alert("Enter the buyer's tax rate for this out-of-state deal (Michigan = 6, tax-exempt = 0).");
      return;
    }
    setLoading(true);
    try {
      let result;
      if (saleType === 'retail') {
        result = await api.createRetailSale({
          vehicleId: selected.id,
          buyer: { name: buyerFullName, address: form.buyerAddress, city: form.buyerCity, state: form.buyerState, zip: form.buyerZip, phone: form.buyerPhone, email: form.buyerEmail },
          purchasePrice: price,
          county: form.county,
          isOutOfState: form.isOutOfState === true,
          outOfStateTaxRate: form.isOutOfState ? (parseFloat(form.outOfStateTaxRate) || 0) : 0,
          tradeInValue: trade,
          docFee: doc,
          fees: [
            { name: 'Temp Tag', amount: tempTag },
            { name: 'Title Fee', amount: titleFee }
          ],
          isDealerDirect: false,
          depositAmount: 0,
          odometer: parseInt(form.odometer) || 0
        });
      } else {
        result = await api.createDeal({
          vehicleId: selected.id,
          saleType: 'wholesale',
          buyerName: buyerFullName,
          buyerContact: form.buyerPhone,
          buyerType: 'dealer',
          salePrice: price,
          paymentMethod: 'Check'
        });
      }
      setCompletedDeal(result?.deal || result);
      setStep(3);
      onComplete();
    } catch (e) {
      alert('Error creating sale: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-xl shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b bg-teal-50 rounded-t-xl flex items-center justify-between">
          <div>
            <h2 className="font-bold text-lg">New Sale</h2>
            <p className="text-xs text-neutral-500">{step < 3 ? `Step ${step} of 2` : 'Complete'}</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-2xl">&times;</button>
        </div>

        {step === 3 ? (
          <div className="p-6 space-y-4 text-center">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
            </div>
            <h3 className="text-lg font-bold text-green-800">Sale Created</h3>
            <p className="text-sm text-neutral-500">{selected?.stockNumber} - {selected?.year} {selected?.make} {selected?.model}</p>
            <p className="text-sm text-neutral-500">Buyer: {[form.buyerFirstName, form.buyerMiddleName, form.buyerLastName].filter(Boolean).join(' ')}</p>

            <div className="space-y-3 pt-2">
              <button
                onClick={() => {
                  if (!completedDeal?.id) { alert('No deal ID available'); return; }
                  setShowPrintPacket(true);
                }}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
              >
                Print Forms…
              </button>

              <a
                href="https://www.ohioautodealers.com"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full px-4 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-medium text-sm text-center"
              >
                Report Temp Tag
              </a>

              <button
                onClick={onClose}
                className="w-full px-4 py-3 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 font-medium text-sm"
              >
                Done
              </button>
            </div>
          </div>
        ) : step === 1 ? (
          <div className="p-4 space-y-4">
            <h3 className="font-semibold text-sm">Select Vehicle</h3>
            <input type="text" placeholder="Search stock#, make, model, VIN..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm" autoFocus/>
            <div className="max-h-48 overflow-y-auto border rounded-lg">
              {filtered.slice(0, 20).map(v => (
                <div key={v.id} onClick={() => { setSelected(v); setForm(f => ({...f, odometer: v.mileage || ''})); setStep(2); }}
                  className={'p-2 hover:bg-teal-50 cursor-pointer border-b last:border-0 ' + (selected?.id === v.id ? 'bg-teal-50' : '')}>
                  <div className="font-medium text-sm">{v.stockNumber} - {v.year} {v.make} {v.model}</div>
                  <div className="text-xs text-neutral-500">{v.color} | {v.vin ? '...' + v.vin.slice(-8) : 'No VIN'}</div>
                </div>
              ))}
              {filtered.length === 0 && <div className="p-3 text-sm text-neutral-400 text-center">No vehicles found</div>}
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Selected vehicle summary */}
            <div className="bg-neutral-50 rounded-lg p-3">
              <div className="font-bold text-sm">{selected.stockNumber} - {selected.year} {selected.make} {selected.model}</div>
              <div className="text-xs text-neutral-500">{selected.color} | VIN: ...{(selected.vin||'').slice(-8)}</div>
              <div className="mt-2">
                <label className="text-xs font-medium text-neutral-600 block mb-1">Odometer Reading</label>
                <input type="number" value={form.odometer} onChange={e => setForm(f => ({...f, odometer: e.target.value}))}
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono" placeholder="Miles"/>
              </div>
            </div>

            {/* Sale Type */}
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setSaleType('retail')}
                className={'p-3 border-2 rounded-lg text-center text-sm ' + (saleType === 'retail' ? 'border-teal-500 bg-teal-50 font-bold' : 'border-neutral-200')}>
                Retail
              </button>
              <button onClick={() => setSaleType('wholesale')}
                className={'p-3 border-2 rounded-lg text-center text-sm ' + (saleType === 'wholesale' ? 'border-teal-500 bg-teal-50 font-bold' : 'border-neutral-200')}>
                Wholesale
              </button>
            </div>

            {/* Buyer Info */}
            <div>
              <label className="text-xs font-medium text-neutral-600 block mb-1">Buyer Name *</label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-neutral-400 block mb-1">First *</label>
                  <input type="text" tabIndex={1} value={form.buyerFirstName} onChange={e => setForm(f => ({...f, buyerFirstName: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="First"/>
                </div>
                <div>
                  <label className="text-xs text-neutral-400 block mb-1">Middle</label>
                  <input type="text" tabIndex={2} value={form.buyerMiddleName} onChange={e => setForm(f => ({...f, buyerMiddleName: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Middle"/>
                </div>
                <div>
                  <label className="text-xs text-neutral-400 block mb-1">Last *</label>
                  <input type="text" tabIndex={3} value={form.buyerLastName} onChange={e => setForm(f => ({...f, buyerLastName: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Last"/>
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 block mb-1">Address *</label>
              <input type="text" tabIndex={4} value={form.buyerAddress} onChange={e => setForm(f => ({...f, buyerAddress: e.target.value}))}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="123 Main St"/>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs font-medium text-neutral-600 block mb-1">City *</label>
                <input type="text" tabIndex={5} value={form.buyerCity} onChange={e => setForm(f => ({...f, buyerCity: e.target.value}))}
                  className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Toledo"/>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 block mb-1">State *</label>
                <input type="text" tabIndex={6} value={form.buyerState} onChange={e => setForm(f => ({...f, buyerState: e.target.value}))}
                  className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="OH"/>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 block mb-1">Zip *</label>
                <input type="text" tabIndex={7} value={form.buyerZip} onChange={e => setForm(f => ({...f, buyerZip: e.target.value}))}
                  className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="43604"/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-neutral-600 block mb-1">Phone</label>
                <input type="text" tabIndex={8} value={form.buyerPhone} onChange={e => setForm(f => ({...f, buyerPhone: e.target.value}))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"/>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 block mb-1">Email</label>
                <input type="text" tabIndex={9} value={form.buyerEmail} onChange={e => setForm(f => ({...f, buyerEmail: e.target.value}))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"/>
              </div>
            </div>

            {/* Pricing */}
            <div>
              <label className="text-xs font-medium text-neutral-600 block mb-1">Sale Price *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                <input type="number" value={form.salePrice} onChange={e => {
                  const newPrice = e.target.value;
                  setForm(f => ({...f, salePrice: newPrice}));
                  setOtdEditing(false);
                  // Forward-calculate OTD from new sale price
                  const p = parseFloat(newPrice) || 0;
                  const tr = parseFloat(form.tradeInValue) || 0;
                  const d = parseFloat(form.docFee) || 0;
                  const txbl = Math.max(0, p - tr);
                  const tR = effectiveTaxRate(form, saleType);
                  const tx = txbl * tR / 100;
                  const tp = saleType === 'retail' ? (parseFloat(form.tempTagFee) || 0) : 0;
                  const tf = parseFloat(form.titleFee) || 0;
                  const newOtd = p + tx + d + tp + tf - tr;
                  setOtdPrice(p > 0 ? newOtd.toFixed(2) : '');
                }}
                  className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm font-mono"/>
              </div>
            </div>

            {/* Out-the-Door Price Calculator */}
            {saleType === 'retail' && (
              <div>
                <label className="text-xs font-medium text-teal-700 block mb-1">Out-the-Door Price</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-teal-500">$</span>
                  <input type="number" value={otdEditing ? otdPrice : (price > 0 ? total.toFixed(2) : '')} onChange={e => {
                    const targetOtd = e.target.value;
                    setOtdPrice(targetOtd);
                    setOtdEditing(true);
                    // Back-calculate sale price from target OTD
                    const otdVal = parseFloat(targetOtd) || 0;
                    const tr = parseFloat(form.tradeInValue) || 0;
                    const d = parseFloat(form.docFee) || 0;
                    const tp = !form.isOutOfState ? (parseFloat(form.tempTagFee) || 0) : 0;
                    const tf = parseFloat(form.titleFee) || 0;
                    const tR = effectiveTaxRate(form, 'retail');
                    // OTD = price + (price - trade) * rate/100 + doc + tempTag + titleFee - trade
                    // OTD = price * (1 + rate/100) - trade * (1 + rate/100) + doc + tempTag + titleFee
                    // price = (OTD - doc - tempTag - titleFee + trade * (1 + rate/100)) / (1 + rate/100)
                    const rateMult = 1 + tR / 100;
                    const backPrice = (otdVal - d - tp - tf + tr * rateMult) / rateMult;
                    setForm(f => ({...f, salePrice: otdVal > 0 ? Math.max(0, backPrice).toFixed(2) : ''}));
                  }}
                    onBlur={() => setOtdEditing(false)}
                    className="w-full pl-7 pr-3 py-2 border-2 border-teal-300 rounded-lg text-sm font-mono bg-teal-50 focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                    placeholder="Enter target OTD to back-calculate price"/>
                </div>
                <p className="text-[10px] text-teal-600 mt-0.5">Type a target OTD to auto-calculate the sale price</p>
              </div>
            )}

            {saleType === 'retail' && (
              <>
                {/* REQUIRED choice: in-state vs out-of-state. Decides the tax math AND
                    which form pack prints. No default — must be answered to finalize. */}
                {form.isOutOfState === null && (
                  <div className="bg-red-50 border-2 border-red-300 rounded-lg px-3 py-2">
                    <p className="text-sm font-bold text-red-700">Is this deal In-State or Out-of-State?</p>
                    <p className="text-[11px] text-red-600">Required before finalizing — it sets the tax and which forms print.</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <button type="button"
                    onClick={() => setForm(f => ({...f, isOutOfState: false}))}
                    className={`px-3 py-2.5 rounded-lg border-2 text-sm font-semibold transition-colors ${
                      form.isOutOfState === false
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white text-neutral-600 border-neutral-300 hover:border-teal-400'
                    }`}>
                    In-State (OH)
                  </button>
                  <button type="button"
                    onClick={() => setForm(f => ({...f, isOutOfState: true}))}
                    className={`px-3 py-2.5 rounded-lg border-2 text-sm font-semibold transition-colors ${
                      form.isOutOfState === true
                        ? 'bg-orange-600 text-white border-orange-600'
                        : 'bg-white text-neutral-600 border-neutral-300 hover:border-orange-400'
                    }`}>
                    Out-of-State
                  </button>
                </div>

                {form.isOutOfState === false && (
                  <div>
                    <label className="text-xs font-medium text-neutral-600 block mb-1">County</label>
                    <select value={form.county} onChange={e => setForm(f => ({...f, county: e.target.value}))}
                      className="w-full px-2 py-2 border rounded-lg text-sm">
                      {Object.keys(taxRates).sort().map(c => <option key={c} value={c}>{c} ({taxRates[c]}%)</option>)}
                    </select>
                  </div>
                )}

                {/* Out-of-state buyer tax: MUST be typed (Michigan = 6, tax-exempt = 0) */}
                {form.isOutOfState === true && (
                  <div>
                    <label className="text-xs font-medium text-neutral-600 block mb-1">
                      Buyer's Tax Rate % <span className="text-red-500">*</span> <span className="text-neutral-400 font-normal">(type it — Michigan = 6, tax-exempt = 0)</span>
                    </label>
                    <input type="number" step="0.25" min="0" max="15" value={form.outOfStateTaxRate}
                      onChange={e => setForm(f => ({...f, outOfStateTaxRate: e.target.value}))}
                      className={`w-full px-3 py-2 border-2 rounded-lg text-sm font-mono ${
                        String(form.outOfStateTaxRate).trim() === '' ? 'border-red-400 bg-red-50' : 'border-neutral-200'
                      }`} placeholder="Required — enter the rate"/>
                    {form.buyerState.trim().toUpperCase() === 'OH' && (
                      <p className="text-[11px] text-orange-600 mt-0.5">Buyer's state is still OH — set it to their home state on the buyer info above.</p>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-neutral-600 block mb-1">Trade-In</label>
                    <input type="number" value={form.tradeInValue} onChange={e => setForm(f => ({...f, tradeInValue: e.target.value}))}
                      className="w-full px-3 py-2 border rounded-lg text-sm font-mono" placeholder="0"/>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600 block mb-1">Doc Fee</label>
                    <input type="number" value={form.docFee} onChange={e => setForm(f => ({...f, docFee: e.target.value}))}
                      className="w-full px-3 py-2 border rounded-lg text-sm font-mono"/>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-neutral-600 block mb-1">Temp Tag Fee</label>
                    <input type="number" value={form.tempTagFee} onChange={e => setForm(f => ({...f, tempTagFee: e.target.value}))}
                      className="w-full px-3 py-2 border rounded-lg text-sm font-mono"/>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600 block mb-1">Title Fee</label>
                    <input type="number" value={form.titleFee} onChange={e => setForm(f => ({...f, titleFee: e.target.value}))}
                      className="w-full px-3 py-2 border rounded-lg text-sm font-mono"/>
                  </div>
                </div>
              </>
            )}

            {/* Totals */}
            {price > 0 && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between"><span>Sale Price</span><span className="font-mono">${price.toLocaleString()}</span></div>
                {trade > 0 && <div className="flex justify-between text-red-600"><span>- Trade-In</span><span className="font-mono">-${trade.toLocaleString()}</span></div>}
                {saleType === 'retail' && <div className="flex justify-between"><span>Sales Tax ({form.isOutOfState === true ? `${taxRate}% out-of-state` : form.isOutOfState === null ? 'choose in/out of state' : taxRate + '%'})</span><span className="font-mono">${tax.toFixed(2)}</span></div>}
                <div className="flex justify-between"><span>Doc Fee</span><span className="font-mono">${doc.toLocaleString()}</span></div>
                {saleType === 'retail' && <div className="flex justify-between"><span>Temp Tag + Title</span><span className="font-mono">${(tempTag + titleFee).toFixed(2)}</span></div>}
                <div className="border-t border-teal-300 my-1"></div>
                <div className="flex justify-between font-bold text-lg"><span>Total</span><span className="font-mono">${total.toLocaleString('en-US', {minimumFractionDigits: 2})}</span></div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button onClick={() => setStep(1)} className="px-4 py-2 bg-neutral-100 rounded-lg hover:bg-neutral-200 text-sm">Back</button>
              <button onClick={handleSubmit}
                disabled={loading || !form.buyerFirstName || !form.salePrice || stateChoiceMissing || oosRateMissing}
                title={stateChoiceMissing ? 'Choose In-State or Out-of-State first' : oosRateMissing ? "Enter the buyer's tax rate" : undefined}
                className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm font-medium">
                {loading ? 'Creating...' : stateChoiceMissing ? 'Choose In/Out-of-State First' : 'Create Sale'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Unified Pre-Print screen for the freshly created deal */}
      {showPrintPacket && completedDeal && (
        <PrintPacketModal mode="deal" deal={completedDeal} onClose={() => setShowPrintPacket(false)} />
      )}
    </div>
  );
};

// Full Edit Deal Modal - all deal fields editable
const EditDealLiteModal = ({ deal, onClose, onSave }) => {
  const [form, setForm] = useState(() => {
    const parts = (deal.buyer_name || '').trim().split(/\s+/).filter(Boolean);
    const first = parts[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1] : '';
    const middle = parts.length > 2 ? parts.slice(1, -1).join(' ') : '';
    return {
      saleType: deal.sale_type || 'retail',
      buyerFirstName: first,
      buyerMiddleName: middle,
      buyerLastName: last,
      buyerAddress: deal.buyer_address || '',
      buyerCity: deal.buyer_city || '',
      buyerState: deal.buyer_state || 'OH',
      buyerZip: deal.buyer_zip || '',
      buyerPhone: deal.buyer_phone || '',
      buyerEmail: deal.buyer_email || '',
      salePrice: deal.sale_price || '',
      saleDate: deal.sale_date ? new Date(deal.sale_date).toISOString().split('T')[0] : '',
      county: deal.county || 'Lucas',
      isOutOfState: deal.is_out_of_state || false,
      tradeInValue: deal.trade_in_value || 0,
      docFee: deal.doc_fee ?? 300,
      tempTagFee: deal.temp_tag_fee ?? 20,
      titleFee: deal.title_fee ?? 22.50,
      salesTax: deal.sales_tax ?? '',
      paymentMethod: deal.payment_method || '',
      odometer: deal.mileage || '',
      notes: deal.notes || ''
    };
  });
  const [vehicleCosts, setVehicleCosts] = useState({ purchasePrice: '', reconCost: '', transportCost: '' });
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState('buyer');
  const [taxRates, setTaxRates] = useState({});
  const [otdPrice, setOtdPrice] = useState('');
  const [otdEditing, setOtdEditing] = useState(false);

  useEffect(() => {
    api.getTaxRates().then(d => setTaxRates(d.rates || d)).catch(() => {});
    if (deal.vehicle_id) {
      api.getVehicle(deal.vehicle_id).then(v => {
        if (v) setVehicleCosts({
          purchasePrice: v.purchasePrice ?? v.purchase_price ?? '',
          reconCost: v.reconCost ?? v.recon_cost ?? '',
          transportCost: v.transportCost ?? v.transport_cost ?? ''
        });
      }).catch(() => {});
    }
  }, [deal.vehicle_id]);

  // Compute totals for OTD display
  const editPrice = parseFloat(form.salePrice) || 0;
  const editTrade = parseFloat(form.tradeInValue) || 0;
  const editDoc = parseFloat(form.docFee) || 0;
  const editTaxRate = (form.saleType === 'retail' && !form.isOutOfState) ? (parseFloat(form.salesTax) > 0 ? 0 : (taxRates[form.county] || 7.75)) : 0;
  const editTaxOverride = parseFloat(form.salesTax) || 0;
  const editTax = editTaxOverride > 0 ? editTaxOverride : (Math.max(0, editPrice - editTrade) * editTaxRate / 100);
  const editTempTag = form.saleType === 'retail' ? (parseFloat(form.tempTagFee) || 0) : 0;
  const editTitleFee = parseFloat(form.titleFee) || 0;
  const editTotal = editPrice + editTax + editDoc + editTempTag + editTitleFee - editTrade;

  const handleSave = async () => {
    setLoading(true);
    try {
      const buyerName = [form.buyerFirstName, form.buyerMiddleName, form.buyerLastName].filter(Boolean).join(' ').trim();
      await api.updateDeal(deal.id, {
        saleType: form.saleType,
        buyerName,
        buyerAddress: form.buyerAddress,
        buyerCity: form.buyerCity,
        buyerState: form.buyerState,
        buyerZip: form.buyerZip,
        buyerPhone: form.buyerPhone,
        buyerEmail: form.buyerEmail,
        salePrice: parseFloat(form.salePrice) || 0,
        saleDate: form.saleDate || null,
        county: form.county,
        isOutOfState: form.isOutOfState,
        tradeInValue: parseFloat(form.tradeInValue) || 0,
        docFee: parseFloat(form.docFee) || 0,
        tempTagFee: parseFloat(form.tempTagFee) || 0,
        titleFee: parseFloat(form.titleFee) || 0,
        salesTax: parseFloat(form.salesTax) || 0,
        paymentMethod: form.paymentMethod,
        odometer: form.odometer ? parseInt(form.odometer) : undefined,
        notes: form.notes
      });
      if (deal.vehicle_id) {
        const vcUpdate = {};
        if (vehicleCosts.purchasePrice !== '') vcUpdate.purchasePrice = parseFloat(vehicleCosts.purchasePrice) || 0;
        if (vehicleCosts.reconCost !== '') vcUpdate.reconCost = parseFloat(vehicleCosts.reconCost) || 0;
        if (vehicleCosts.transportCost !== '') vcUpdate.transportCost = parseFloat(vehicleCosts.transportCost) || 0;
        if (Object.keys(vcUpdate).length > 0) await api.updateVehicle(deal.vehicle_id, vcUpdate);
      }
      onSave();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setLoading(false); }
  };

  const tabs = [
    { id: 'buyer', label: 'Buyer' },
    { id: 'sale', label: 'Sale & Fees' },
    { id: 'costs', label: 'Costs' },
    { id: 'other', label: 'Other' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="font-bold">Edit Deal</h2>
            <p className="text-xs text-neutral-500">{deal.stock_number} - {deal.year} {deal.make} {deal.model}</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-2xl">&times;</button>
        </div>

        <div className="flex border-b">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setSection(t.id)}
              className={`flex-1 px-3 py-2 text-xs font-medium border-b-2 -mb-px ${section === t.id ? 'border-teal-500 text-teal-700' : 'border-transparent text-neutral-500 hover:text-neutral-700'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {section === 'buyer' && (
            <>
              <div>
                <label className="text-xs font-medium text-neutral-600 block mb-1">Buyer Name *</label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-neutral-400 block mb-1">First *</label>
                    <input type="text" tabIndex={1} value={form.buyerFirstName} onChange={e => setForm(f => ({...f, buyerFirstName: e.target.value}))}
                      className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="First"/>
                  </div>
                  <div>
                    <label className="text-xs text-neutral-400 block mb-1">Middle</label>
                    <input type="text" tabIndex={2} value={form.buyerMiddleName} onChange={e => setForm(f => ({...f, buyerMiddleName: e.target.value}))}
                      className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Middle"/>
                  </div>
                  <div>
                    <label className="text-xs text-neutral-400 block mb-1">Last *</label>
                    <input type="text" tabIndex={3} value={form.buyerLastName} onChange={e => setForm(f => ({...f, buyerLastName: e.target.value}))}
                      className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Last"/>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 block mb-1">Address</label>
                <input type="text" value={form.buyerAddress} onChange={e => setForm(f => ({...f, buyerAddress: e.target.value}))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"/>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">City</label>
                  <input type="text" value={form.buyerCity} onChange={e => setForm(f => ({...f, buyerCity: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"/>
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">State</label>
                  <input type="text" value={form.buyerState} onChange={e => setForm(f => ({...f, buyerState: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"/>
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">Zip</label>
                  <input type="text" value={form.buyerZip} onChange={e => setForm(f => ({...f, buyerZip: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">Phone</label>
                  <input type="text" value={form.buyerPhone} onChange={e => setForm(f => ({...f, buyerPhone: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"/>
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">Email</label>
                  <input type="text" value={form.buyerEmail} onChange={e => setForm(f => ({...f, buyerEmail: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"/>
                </div>
              </div>
            </>
          )}

          {section === 'sale' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">Sale Type</label>
                  <select value={form.saleType} onChange={e => setForm(f => ({...f, saleType: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="retail">Retail</option>
                    <option value="wholesale">Wholesale</option>
                    <option value="wholesale_direct">Wholesale Direct</option>
                    <option value="wholesale_auction">Wholesale Auction</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">Sale Date</label>
                  <input type="date" value={form.saleDate} onChange={e => setForm(f => ({...f, saleDate: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"/>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 block mb-1">Sale Price *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                  <input type="number" value={form.salePrice} onChange={e => {
                    setForm(f => ({...f, salePrice: e.target.value}));
                    setOtdEditing(false);
                  }}
                    className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm font-mono"/>
                </div>
              </div>
              {form.saleType === 'retail' && (
                <div>
                  <label className="text-xs font-medium text-teal-700 block mb-1">Out-the-Door Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-teal-500">$</span>
                    <input type="number" value={otdEditing ? otdPrice : (editPrice > 0 ? editTotal.toFixed(2) : '')} onChange={e => {
                      const targetOtd = e.target.value;
                      setOtdPrice(targetOtd);
                      setOtdEditing(true);
                      const otdVal = parseFloat(targetOtd) || 0;
                      const tr = parseFloat(form.tradeInValue) || 0;
                      const d = parseFloat(form.docFee) || 0;
                      const tp = !form.isOutOfState ? (parseFloat(form.tempTagFee) || 0) : 0;
                      const tf = parseFloat(form.titleFee) || 0;
                      const txOver = parseFloat(form.salesTax) || 0;
                      if (txOver > 0) {
                        // Fixed tax: just subtract fixed amounts
                        const backPrice = otdVal - d - tp - tf - txOver + tr;
                        setForm(f => ({...f, salePrice: otdVal > 0 ? Math.max(0, backPrice).toFixed(2) : ''}));
                      } else {
                        const tR = !form.isOutOfState ? (taxRates[form.county] || 7.75) : 0;
                        const rateMult = 1 + tR / 100;
                        const backPrice = (otdVal - d - tp - tf + tr * rateMult) / rateMult;
                        setForm(f => ({...f, salePrice: otdVal > 0 ? Math.max(0, backPrice).toFixed(2) : ''}));
                      }
                    }}
                      onBlur={() => setOtdEditing(false)}
                      className="w-full pl-7 pr-3 py-2 border-2 border-teal-300 rounded-lg text-sm font-mono bg-teal-50 focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                      placeholder="Enter target OTD to back-calculate price"/>
                  </div>
                  <p className="text-[10px] text-teal-600 mt-0.5">Type a target OTD to auto-calculate the sale price</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">County</label>
                  <input type="text" value={form.county} onChange={e => setForm(f => ({...f, county: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"/>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.isOutOfState} onChange={e => setForm(f => ({...f, isOutOfState: e.target.checked}))}/>
                    <span className="text-sm">Out of State</span>
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">Trade-In Value</label>
                  <input type="number" value={form.tradeInValue} onChange={e => setForm(f => ({...f, tradeInValue: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm font-mono"/>
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">Doc Fee</label>
                  <input type="number" value={form.docFee} onChange={e => setForm(f => ({...f, docFee: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm font-mono"/>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">Temp Tag Fee</label>
                  <input type="number" value={form.tempTagFee} onChange={e => setForm(f => ({...f, tempTagFee: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm font-mono"/>
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">Title Fee</label>
                  <input type="number" value={form.titleFee} onChange={e => setForm(f => ({...f, titleFee: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm font-mono"/>
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">Sales Tax</label>
                  <input type="number" value={form.salesTax} onChange={e => setForm(f => ({...f, salesTax: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm font-mono"/>
                </div>
              </div>
            </>
          )}

          {section === 'costs' && (
            <>
              <div>
                <label className="text-xs font-medium text-neutral-600 block mb-1">Purchase Price</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                  <input type="number" step="0.01" value={vehicleCosts.purchasePrice} onChange={e => setVehicleCosts(v => ({ ...v, purchasePrice: e.target.value }))}
                    className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm font-mono"/>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 block mb-1">Recon Cost</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                  <input type="number" step="0.01" value={vehicleCosts.reconCost} onChange={e => setVehicleCosts(v => ({ ...v, reconCost: e.target.value }))}
                    className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm font-mono"/>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 block mb-1">Transport Cost</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                  <input type="number" step="0.01" value={vehicleCosts.transportCost} onChange={e => setVehicleCosts(v => ({ ...v, transportCost: e.target.value }))}
                    className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm font-mono"/>
                </div>
              </div>
            </>
          )}

          {section === 'other' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">Payment Method</label>
                  <select value={form.paymentMethod} onChange={e => setForm(f => ({...f, paymentMethod: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="">Select...</option>
                    <option value="Cash">Cash</option>
                    <option value="Check">Check</option>
                    <option value="Wire">Wire</option>
                    <option value="Finance">Finance</option>
                    <option value="Card">Card</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600 block mb-1">Odometer</label>
                  <input type="number" value={form.odometer} onChange={e => setForm(f => ({...f, odometer: e.target.value}))}
                    className="w-full px-3 py-2 border rounded-lg text-sm font-mono" placeholder="Miles"/>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 block mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                  className="w-full px-3 py-2 border rounded-lg text-sm h-24 resize-none"/>
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-neutral-100 rounded-lg hover:bg-neutral-200 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={loading} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm">
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DealDeskLite;
